/* eslint-disable sort-exports/sort-exports */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import DataGrid from 'components/DataGrid';
import { useSafeQuery } from 'hooks/useSafeQuery';
import useGetShippingColumns from 'pages/Shipping/hooks/useGetShippingColumns';
import {
  GridCallbackDetails,
  GridPaginationModel,
  useGridApiRef,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import {
  PaginationChangeReason,
  ENABLE_HORIZONTAL_SCROLLING_CLASS_NAME,
} from 'components/DataGrid/constants';
import { GENERIC_PAGE_SIZE_OPTIONS } from 'global-constants';
import * as ShippingTableStyles from './styles';
import { NewOrderItem } from 'types';
import {
  selectSelectedOrderItemIds,
  setSelectedOrderItems as setReduxSelectedOrderItems,
  clearSelectedOrderItems,
} from 'store/slices/shipping/selectedOrderItems';
import { SHIPPING_TABLE_PAGINATION_MODEL } from 'pages/Shipping/constants';
import { useNotification } from '../../../../hooks/useNotification';
import { storeOriginalOrderItems } from 'store/slices/shipping/orderItemChanges';
import { GET_PAGINATED_ORDER_ITEMS } from 'pages/Shipping/queries';
import { Dispatch, SetStateAction } from 'react';
import { ShipmentCarrier } from 'api/rest/shipmentCarriers/types';

interface Props {
  setSelectedOrderItems: Dispatch<SetStateAction<NewOrderItem[]>>;
  selectedOrderId: string | null;
  carriers: ShipmentCarrier[];
  loadingCarriers: boolean;
  setRefetchOrderItems?: (refetch: () => Promise<any>) => void;
  disablePagination?: boolean;
}

const ShippingTable: React.FC<Props> = ({
  setSelectedOrderItems,
  selectedOrderId,
  carriers = [],
  loadingCarriers,
  setRefetchOrderItems,
  disablePagination = false,
}) => {
  const apiRef = useGridApiRef();
  const styles = ShippingTableStyles;
  const dispatch = useDispatch();
  const { showError } = useNotification();

  // Get selected order item IDs from Redux store
  const selectedOrderItemIds = useSelector(selectSelectedOrderItemIds);

  // Add a ref to track previous selection to prevent infinite updates
  const prevSelectionRef = React.useRef('');

  // Add state to preserve data during loading
  const [preservedRows, setPreservedRows] = React.useState<NewOrderItem[]>([]);
  const [preservedRowCount, setPreservedRowCount] = React.useState(0);

  //Pagination -- see Employees Datagrid for more details
  const [paginationModel, setPaginationModel] =
    React.useState<GridPaginationModel>(SHIPPING_TABLE_PAGINATION_MODEL);

  const handleOnPaginationModelChange = (
    model: GridPaginationModel,
    details: GridCallbackDetails
  ) => {
    // If pagination is disabled, don't allow changes
    if (disablePagination) {
      return;
    }

    if (details?.reason === PaginationChangeReason.SET_PAGINATION_MODEL) {
      // Clear selected items when page changes
      dispatch(clearSelectedOrderItems());
      setPaginationModel(model);
    }
  };

  // Handle row selection changes
  const handleRowSelectionModelChange = (
    newSelectionModel: GridRowSelectionModel
  ) => {
    // Convert all IDs to strings to ensure consistent type
    const selectedIds = newSelectionModel.map((id) => String(id));
    dispatch(setReduxSelectedOrderItems(selectedIds));
  };

  const columns = useGetShippingColumns({
    carriers: carriers || [],
    loadingCarriers,
  });

  /* START: Query SECTION */
  const {
    data: orderItemsData,
    loading: loadingOrderItemsData,
    refetch: refetchPaginatedOrderItems,
  } = useSafeQuery(GET_PAGINATED_ORDER_ITEMS, {
    variables: {
      take: paginationModel.pageSize,
      skip: paginationModel.page * paginationModel.pageSize,
      where: selectedOrderId
        ? { orderId: { equals: selectedOrderId } }
        : undefined,
    },
    nextFetchPolicy: 'network-only',
    onError(error) {
      showError('Failed to load order items');
    },
  });

  // Pass the refetch function to the parent component
  React.useEffect(() => {
    if (setRefetchOrderItems && refetchPaginatedOrderItems) {
      setRefetchOrderItems(refetchPaginatedOrderItems);
    }
  }, [refetchPaginatedOrderItems, setRefetchOrderItems]);

  // Reset pagination and clear selected items when selectedOrderId changes
  React.useEffect(() => {
    setPaginationModel((prevModel) => ({
      ...prevModel,
      page: 0,
    }));
    dispatch(clearSelectedOrderItems());
  }, [selectedOrderId, dispatch]);

  const orderItems: NewOrderItem[] =
    orderItemsData?.v2FindAndCountOrderItems?.orderItems || [];

  // Update preserved data when we get new data
  React.useEffect(() => {
    if (orderItems.length > 0) {
      setPreservedRows(orderItems);
    }
  }, [orderItems]);

  // Store original order items in Redux when they're loaded
  React.useEffect(() => {
    if (orderItems.length > 0) {
      // Always dispatch the latest order items to ensure we have up-to-date data
      dispatch(storeOriginalOrderItems(orderItems));
    }
  }, [orderItems, dispatch]);

  const totalRowCount = orderItemsData?.v2FindAndCountOrderItems?.count || 0;

  // Update preserved row count when we get new data
  React.useEffect(() => {
    if (totalRowCount > 0) {
      setPreservedRowCount(totalRowCount);
    }
  }, [totalRowCount]);

  const loadingOrderItemTable = loadingOrderItemsData || loadingCarriers;

  // Use preserved data during loading
  const effectiveRows = loadingOrderItemTable ? preservedRows : orderItems;
  const effectiveRowCount = loadingOrderItemTable
    ? preservedRowCount
    : totalRowCount;

  // Update selected order items data for parent component
  React.useEffect(() => {
    const selectedOrderItemsData = orderItems.filter((orderItem) =>
      selectedOrderItemIds.includes(String(orderItem.id))
    );

    // Use JSON.stringify to compare arrays by value rather than reference
    const currentSelection = JSON.stringify(
      selectedOrderItemsData.map((item) => item.id)
    );

    // Only update if the selection has actually changed
    if (prevSelectionRef.current !== currentSelection) {
      setSelectedOrderItems(selectedOrderItemsData);
      prevSelectionRef.current = currentSelection;
    }
  }, [selectedOrderItemIds, orderItems, setSelectedOrderItems]);

  return (
    <Box sx={styles.ShippingTableDataGridBoxSx}>
      <DataGrid
        apiRef={apiRef}
        rows={effectiveRows}
        rowCount={effectiveRowCount}
        columns={columns}
        autoHeight={false}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handleOnPaginationModelChange}
        sortingMode="server"
        disableMultipleColumnsSorting
        checkboxSelection
        rowSelectionModel={selectedOrderItemIds}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        loading={loadingOrderItemTable}
        pageSizeOptions={GENERIC_PAGE_SIZE_OPTIONS}
        hideFooterSelectedRowCount
        keepNonExistentRowsSelected
        disableRowSelectionOnClick
        editMode="cell"
        getRowClassName={(params) => {
          return selectedOrderItemIds.includes(String(params.id))
            ? 'selected-row'
            : '';
        }}
        sx={styles.ShippingTableDataGridBoxSx}
        slotProps={{
          panel: {
            placement: 'bottom-end',
          },
          filterPanel: {
            sx: { visibility: 'hidden' },
          },
          pagination: {
            nextIconButtonProps: {
              disabled:
                disablePagination ||
                paginationModel.page >=
                  Math.ceil(effectiveRowCount / paginationModel.pageSize) - 1,
            },
            backIconButtonProps: {
              disabled: disablePagination || paginationModel.page <= 0,
            },
          },
        }}
        classes={{
          virtualScroller: ENABLE_HORIZONTAL_SCROLLING_CLASS_NAME,
        }}
      />
    </Box>
  );
};

export default ShippingTable;
